import React from "react";
import ReactApexChart from "react-apexcharts";
import { FaCalendarAlt, FaFlask, FaPills, FaSms } from "react-icons/fa";
import StatCard from "../../../components/cards/statCard";

// ---------------- Skeletons ----------------
const DonutSkeleton = () => (
    <div className="bg-white rounded-lg p-4 h-[280px] flex items-center justify-center animate-pulse">
        <div className="w-40 h-40 rounded-full bg-gray-200" />
    </div>
);

const StatCardSkeleton = () => (
    <div className="bg-white rounded-md p-5 animate-pulse drop-shadow-[0_0px_20px_rgba(203,229,255,0.5)]">
        <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-gray-200" />
            <div className="h-4 w-2/3 bg-gray-200 rounded" />
        </div>
        <div className="h-8 w-1/2 bg-gray-300 rounded" />
        <div className="h-3 w-1/3 bg-gray-200 rounded mt-2" />
    </div>
);

// ---------------- Component ----------------
const ActivityDonutChart = ({
                                totalMedicines = 0,
                                totalLabTests = 0,
                                totalAppointments = 0,
                                totalSMS = 0,
                                growth = {},
                                loading = false
                            }) => {

    const series = [
        totalMedicines,
        totalLabTests,
        totalAppointments,
        totalSMS
    ];

    const options = {
        chart: {
            type: "donut",
            height: 280,
            fontFamily: "Inter, sans-serif"
        },
        labels: ["Medicines", "Lab Tests", "Appointments", "SMS"],
        colors: ["#ec4899", "#06b6d4", "#facc15", "#22c55e"],
        legend: {
            position: "bottom",
            fontSize: "12px"
        },
        dataLabels: {
            enabled: true,
            formatter: (val) => `${Math.round(val)}%`,
            style: {
                fontSize: "11px",
                fontWeight: 600
            }
        },
        plotOptions: {
            pie: {
                donut: {
                    size: "65%",
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: "Total Activity"
                        }
                    }
                }
            }
        }
    };

    return (
        <div className="flex flex-col gap-4 h-fit">
            <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                <FaCalendarAlt className="text-primary" />
                Activity Statistics
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

                {/* Cards */}
                <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {loading ? (
                        <>
                            <StatCardSkeleton />
                            <StatCardSkeleton />
                            <StatCardSkeleton />
                            <StatCardSkeleton />
                        </>
                    ) : (
                        <>
                            <StatCard
                                title="Total Medicines Prescribed"
                                count={totalMedicines}
                                growth={growth.medicines}
                                icon={<FaPills className="w-5 h-5 text-pink-500" />}
                                bgColor="bg-pink-100"
                            />

                            <StatCard
                                title="Total Lab Tests Prescribed"
                                count={totalLabTests}
                                growth={growth.labTests}
                                icon={<FaFlask className="w-5 h-5 text-cyan-500" />}
                                bgColor="bg-cyan-100"
                            />

                            <StatCard
                                title="Total Appointments"
                                count={totalAppointments}
                                growth={growth.appointments}
                                icon={<FaCalendarAlt className="w-5 h-5 text-yellow-500" />}
                                bgColor="bg-yellow-100"
                            />

                            <StatCard
                                title="Total SMS Sent"
                                count={totalSMS}
                                growth={growth.sms}
                                icon={<FaSms className="w-5 h-5 text-green-600" />}
                                bgColor="bg-green-50"
                            />
                        </>
                    )}
                </div>

                {/* Donut */}
                {loading ? (
                    <DonutSkeleton />
                ) : (
                    <div className="bg-white rounded-lg p-4 drop-shadow-[0_0px_20px_rgba(203,229,255,0.5)]">
                        <ReactApexChart
                            options={options}
                            series={series}
                            type="donut"
                            height={280}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ActivityDonutChart;
